import { useState, useEffect, useContext } from "react";
import { DashboardLayout } from "@/components/layout/dashboard-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Loader2,
  CheckCircle,
  XCircle,
  Star
} from "lucide-react";
import { vendorService, countryService, stateService } from "@/services/api";
import { Vendor, VendorFormData, SelectData } from "@/types/api";
import { useToast } from "@/hooks/use-toast";

const VendorsPage = () => {
  const { toast } = useToast();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [filteredVendors, setFilteredVendors] = useState<Vendor[]>([]);
  const [countries, setCountries] = useState<SelectData[]>([]);
  const [states, setStates] = useState<SelectData[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingCountries, setIsLoadingCountries] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState<VendorFormData>({
    name: "",
    storeName: "",
    countryId: 0,
    countryName: "",
    stateId: 0,
    stateName: "",
    street: "",
    city: "",
    pincode: "",
    phone: "",
    email: "",
    default: false,
    active: true
  });
  const [isEditing, setIsEditing] = useState(false);

  // Fetch vendors and countries on component mount
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setIsLoadingCountries(true);
      try {
        const [vendorsData, countriesData] = await Promise.all([
          await vendorService.getItems(),
          await countryService.getForSelect()
        ]);
        setVendors(vendorsData);
        setFilteredVendors(vendorsData);
        setCountries(countriesData);
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Fetch Vendors",
          description: error instanceof Error ? error.message : error
        });
      } finally {
        setIsLoading(false);
        setIsLoadingCountries(false);
      }
    };

    fetchData();
  }, []);

  // Update states when country changes
  useEffect(() => {
    const fetchStates = async () => {
      if (formData.countryId > 0) {
        const selectedCountry = countries.find(c => c.id === formData.countryId);
        if (selectedCountry) {
          const states = await stateService.getForSelect(formData.countryId);
          setStates(states);
          // Reset state if country changes
          if (!isEditing) {
            setFormData(prev => ({ ...prev, state: "" }));
          }
        }
      } else {
        setStates([]);
      }
    };

    fetchStates();
  }, [formData.countryId, countries, isEditing]);

  // Filter vendors based on search query
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredVendors(vendors);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = vendors.filter(vendor =>
        vendor.name.toLowerCase().includes(query) ||
        vendor.storeName.toLowerCase().includes(query) ||
        vendor.email.toLowerCase().includes(query) ||
        vendor.phone.includes(query) ||
        vendor.city.toLowerCase().includes(query)
      );
      setFilteredVendors(filtered);
    }
  }, [searchQuery, vendors]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSwitchChange = (name: string, checked: boolean) => {
    setFormData({
      ...formData,
      [name]: checked
    });
  };

  const handleCountryChange = (countryId: number) => {
    const countryName = countries.find(c => c.id === countryId).name;
    setFormData({
      ...formData,
      countryId,
      countryName
    });
  };

  const handleStateChange = (stateId: number) => {
    const stateName = states.find(s => s.id === stateId).name;
    setFormData({
      ...formData,
      stateId,
      stateName
    });
  };

  const handleAddVendor = () => {
    setIsEditing(false);
    setFormData({
      name: "",
      storeName: "",
      countryId: 0,
      countryName: "",
      stateId: 0,
      stateName: "",
      street: "",
      city: "",
      pincode: "",
      phone: "",
      email: "",
      default: false,
      active: true
    });
    setIsDialogOpen(true);
  };

  const handleEditVendor = (vendor: Vendor) => {
    setIsEditing(true);
    setFormData({
      id: vendor.id,
      name: vendor.name,
      storeName: vendor.storeName,
      countryId: vendor.countryId,
      countryName: vendor.countryName,
      stateId: vendor.stateId,
      stateName: vendor.stateName,
      street: vendor.street,
      city: vendor.city,
      pincode: vendor.pincode,
      phone: vendor.phone,
      email: vendor.email,
      default: vendor.default,
      active: vendor.active
    });
    setIsDialogOpen(true);
  };

  const handleDeleteVendor = async (vendorId: number) => {
    if (window.confirm("Are you sure you want to delete this vendor?")) {
      const affectedRows: number = await vendorService.deleteItem(vendorId);
      if (affectedRows > 0) {
        toast({
          title: "Delete Vendor",
          description: "Deleted successfully",
        });
        setVendors(vendors.filter(vendor => vendor.id !== vendorId));
      }
      else 
      toast({
        variant: "destructive",
        title: "Delete Vendor",
        description: "Unable to delete",
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.countryId || !formData.stateId) {
      toast({
        variant: "destructive",
        title: "Save Vendor",
        description: "Please select both country and state"
      });
      return;
    }

    const selectedCountry = countries.find(c => c.id === formData.countryId);
    if (!selectedCountry) return;

    const selectedState = states.find(s => s.id === formData.stateId);
    if (!selectedState) return;

    // If this vendor is set as default, update other vendors
    if (formData.default) {
      setVendors(vendors.map(vendor => ({
        ...vendor,
        default: false
      })));
    }

    if (isEditing) {
      await vendorService.updateItem(formData);
      toast({
        title: "Update Vendor",
        description: "Updated successfully",
      });
      setVendors(vendors.map(vendor =>
        vendor.id === formData.id
          ? {
            ...formData as Vendor,
            countryId: selectedCountry.id,
            countryName: selectedCountry.name,
            stateId: selectedState.id,
            stateName: selectedState.name
          }
          : formData.default ? { ...vendor, default: false } : vendor
      ));
    } else {
      const newVendor: Vendor = {
        id: 0,
        name: formData.name,
        storeName: formData.storeName,
        countryId: selectedCountry.id,
        countryName: selectedCountry.name,
        stateId: selectedState.id,
        stateName: selectedState.name,
        street: formData.street,
        city: formData.city,
        pincode: formData.pincode,
        phone: formData.phone,
        email: formData.email,
        default: formData.default,
        active: formData.active
      };
      newVendor.id = await vendorService.createItem(newVendor);
      if (newVendor.id > 0) {
        toast({
          title: "Add Vendor",
          description: "Added successfully",
        });
        setVendors([...vendors.map(vendor =>
          formData.default ? { ...vendor, default: false } : vendor
        ), newVendor]);
      }
    }
    setIsDialogOpen(false);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Vendor Management</h1>
            <p className="text-muted-foreground">Manage suppliers and vendors</p>
          </div>
          <Button onClick={handleAddVendor}>
            <Plus size={16} className="mr-2" />
            Add Vendor
          </Button>
        </div>

        {/* Search bar */}
        <div className="relative w-full md:w-96">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search vendors..."
            className="pl-8 w-full"
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </div>

        {/* Vendors table */}
        <div className="border rounded-md overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Store Name</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Default</TableHead>
                <TableHead>Active</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10">
                    <div className="flex justify-center">
                      <Loader2 size={30} className="animate-spin text-primary" />
                    </div>
                    <p className="mt-2 text-muted-foreground">Loading vendors...</p>
                  </TableCell>
                </TableRow>
              ) : filteredVendors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                    {searchQuery ? "No vendors found matching your search" : "No vendors found"}
                  </TableCell>
                </TableRow>
              ) : (
                filteredVendors.map((vendor) => (
                  <TableRow key={vendor.id}>
                    <TableCell className="font-medium">{vendor.name}</TableCell>
                    <TableCell>{vendor.storeName}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span>{vendor.city}, {vendor.stateName}</span>
                        <span className="text-xs text-muted-foreground">{vendor.countryName}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span>{vendor.phone}</span>
                        <span className="text-xs text-muted-foreground">{vendor.email}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {vendor.default ? (
                        <Star size={18} className="text-yellow-500 fill-yellow-500" />
                      ) : (
                        <Star size={18} className="text-muted-foreground" />
                      )}
                    </TableCell>
                    <TableCell>
                      {vendor.active ? (
                        <CheckCircle size={18} className="text-green-500" />
                      ) : (
                        <XCircle size={18} className="text-red-500" />
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditVendor(vendor)}
                        >
                          <Edit size={16} className="text-blue-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteVendor(vendor.id)}
                        >
                          <Trash2 size={16} className="text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Add/Edit Vendor Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>{isEditing ? "Edit Vendor" : "Add New Vendor"}</DialogTitle>
              <DialogDescription>
                {isEditing
                  ? "Update vendor information"
                  : "Fill in the information to create a new vendor"}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit}>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Vendor Name</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="storeName">Store Name</Label>
                    <Input
                      id="storeName"
                      name="storeName"
                      value={formData.storeName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="countryId">Country</Label>
                    <Select
                      value={formData.countryId.toString()}
                      onValueChange={(value) => handleCountryChange(Number(value))}
                      disabled={isLoadingCountries}
                    >
                      <SelectTrigger id="countryId">
                        <SelectValue placeholder={isLoadingCountries ? "Loading countries..." : "Select a country"} />
                      </SelectTrigger>
                      <SelectContent>
                        {countries.map(country => (
                          <SelectItem key={country.id} value={country.id.toString()}>
                            {country.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="stateId">State</Label>
                    <Select
                      value={formData.stateId.toString()}
                      onValueChange={(value) => handleStateChange(Number(value))}
                      disabled={formData.countryId === 0 || states.length === 0}
                    >
                      <SelectTrigger id="stateId">
                        <SelectValue placeholder={
                          formData.countryId === 0
                            ? "Select country first"
                            : states.length === 0
                              ? "Loading states..."
                              : "Select a state"
                        } />
                      </SelectTrigger>
                      <SelectContent>
                        {states.map(state => (
                          <SelectItem key={state.id} value={state.id.toString()}>
                            {state.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="street">Street Address</Label>
                  <Input
                    id="street"
                    name="street"
                    value={formData.street}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pincode">Pincode / ZIP</Label>
                    <Input
                      id="pincode"
                      name="pincode"
                      value={formData.pincode}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="default">Default Vendor</Label>
                  <Switch
                    id="default"
                    checked={formData.default}
                    onCheckedChange={(checked) => handleSwitchChange('default', checked)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="active">Active</Label>
                  <Switch
                    id="active"
                    checked={formData.active}
                    onCheckedChange={(checked) => handleSwitchChange('active', checked)}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={formData.countryId ===0 || formData.stateId === 0}>
                  {isEditing ? "Update Vendor" : "Create Vendor"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default VendorsPage;